/* import __COLOCATED_TEMPLATE__ from './company.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type CompanySummary from 'embercom/objects/inbox/company-summary';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import type CompanyApi from 'embercom/services/company-api';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import type Session from 'embercom/services/session';

export interface Args {
  conversationEntry: ConversationTableEntry;
  companyFilter?: CompanySummary;
}

interface Signature {
  Args: Args;
  Element: HTMLParagraphElement;
}

export default class Company extends Component<Signature> {
  @service declare companyApi: CompanyApi;
  @service declare session: Session;

  get hasCompanyConversationAssociationFF() {
    return this.session.workspace.isFeatureEnabled('hsg-inbox-conversation-company-association');
  }

  get companyId() {
    return this.args.conversationEntry.companyId;
  }

  get companyName() {
    if (this.args.companyFilter) {
      return this.args.companyFilter.name;
    }

    let companyId = this.getCompanyIdForDisplay;
    let explicitCompanyName = companyId ? this.findCompanyName(companyId) : undefined;

    // Return explicit company name early if it exists
    if (explicitCompanyName) {
      return explicitCompanyName;
    }

    // Otherwise use fallback if appropriate
    return this.shouldShowFallbackCompany ? this.fallbackCompanyName : undefined;
  }

  get shouldShowFallbackCompany() {
    if (this.args.conversationEntry.isTicket) {
      return false;
    }
    return true;
  }

  get getCompanyIdForDisplay() {
    // For tickets, always use explicit company ID only (no fallback)
    if (this.args.conversationEntry.isTicket) {
      return this.args.conversationEntry.explicitCompanyId;
    }

    // For conversations, use feature flag logic
    return this.hasCompanyConversationAssociationFF
      ? this.companyId
      : this.args.conversationEntry.explicitCompanyId;
  }

  get isUnsafeAssociation() {
    if (this.args.conversationEntry.ticketCategory) {
      return false;
    }

    let allCompanyIds = this.args.conversationEntry.participantSummaries
      .flatMap((participantSummary) => participantSummary.companyIds)
      .uniq();
    return allCompanyIds.length > 1;
  }

  findCompanyName(companyId: string): string | undefined {
    let companyFromUser = this.args.conversationEntry.user?.companies?.find(
      (company) => company.id === companyId,
    );
    if (companyFromUser) {
      return companyFromUser.name;
    }

    let companyFromParticipant = this.findCompanyInParticipants(companyId);
    if (companyFromParticipant) {
      return companyFromParticipant.name;
    }

    if (this.fetchCompany.value) {
      return this.fetchCompany.value.name;
    }

    return undefined;
  }

  findCompanyInParticipants(companyId: string): CompanySummary | undefined {
    return this.args.conversationEntry.participantSummaries
      .flatMap((participant) => participant.companies || [])
      .find((company) => company.id === companyId);
  }

  get fallbackCompanyName() {
    // First try the main user's company (original behavior)
    if (this.args.conversationEntry.companyName) {
      return this.args.conversationEntry.companyName;
    }

    // If main user has no company, look for any participant company
    let participantCompanies = this.args.conversationEntry.participantSummaries.flatMap(
      (participant) => participant.companies || [],
    );

    return participantCompanies[0]?.name;
  }

  get isFallbackCompany() {
    // Don't show fallback company tooltip for tickets
    if (this.args.conversationEntry.isTicket) {
      return false;
    }

    let hasNoExplicitAssociation = !this.args.conversationEntry.explicitCompanyId;
    let hasAnyCompany = this.fallbackCompanyName;

    return hasNoExplicitAssociation && hasAnyCompany;
  }

  get numberOfRelatedCompanies() {
    return this.args.conversationEntry.parent?.userSummary?.companyIds
      ? this.args.conversationEntry.parent.userSummary.companyIds.length
      : 0;
  }

  @use fetchCompany = AsyncData<CompanySummary | undefined | null>(async () => {
    if (!this.companyId) {
      return undefined;
    }

    let company = await this.companyApi.fetchCompany(this.companyId);
    return company;
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationsTable::TableCells::Company': typeof Company;
    'inbox2/conversations-table/table-cells/company': typeof Company;
  }
}
